/**
 * 波形バー描画の共通ルーチン（Canvas 2D）。
 *
 * WaveformCanvas（プレビュー・Messenger 等）とタイムラインの WaveformTrack が共有し、
 * アプリ全体で波形の見た目（バー形状）を一致させる。
 * 座標系は CSS ピクセル前提（DPR スケールは呼び出し側で ctx.scale 済みであること）。
 */

/** バー形状の既定値（従来の wavesurfer 設定 barWidth 2 / barGap 1 / barRadius 2 相当） */
export const WAVEFORM_BAR_DEFAULTS = {
  barWidth: 2,
  barGap: 1,
  barRadius: 2,
} as const;

/** 既定カラー（soremo-light-gray / soremo-blue） */
export const WAVEFORM_COLORS = {
  wave: '#a7a8a9',
  progress: '#009ace',
} as const;

export interface DrawWaveformBarsOptions {
  /** 各チャンネルの min/max 交互配列。複数チャンネルは外包絡にマージして 1 レーンに描く */
  channels: number[][];
  /** 描画先レーンの左端（CSS px） */
  x: number;
  /** 描画先レーンの上端（CSS px） */
  y: number;
  /** レーン幅（CSS px） */
  width: number;
  /** レーン高さ（CSS px） */
  height: number;
  /** ピーク読み出し開始位置（ペア単位、小数可。省略時 0） */
  startPair?: number;
  /** ピーク読み出し終了位置（ペア単位、小数可。省略時は全ペア） */
  endPair?: number;
  barWidth?: number;
  barGap?: number;
  barRadius?: number;
  color: string;
  /** 振幅の表示スケール（クリップゲイン可視化等。既定 1） */
  amplitudeScale?: number;
  /**
   * バー格子の位相オフセット（px）。スクロール窓描画時に絶対座標へバーを揃えるために
   * `-(scrollLeft % (barWidth + barGap))` 等を渡す。省略時 0
   */
  barPhase?: number;
}

/** ペア範囲 [t0, t1) の min/max を計算する。範囲が 1 ペア未満なら線形補間で滑らかにする */
function samplePairRange(
  channels: number[][],
  pairCount: number,
  t0: number,
  t1: number,
): { min: number; max: number } | null {
  if (pairCount <= 0 || t1 <= 0 || t0 >= pairCount) return null;

  if (t1 - t0 >= 1) {
    let p0 = Math.max(0, Math.floor(t0));
    let p1 = Math.min(pairCount, Math.ceil(t1));
    if (p1 <= p0) p1 = p0 + 1;
    let min = 0;
    let max = 0;
    for (const data of channels) {
      for (let p = p0; p < p1; p++) {
        const lo = data[p * 2];
        const hi = data[p * 2 + 1];
        if (lo < min) min = lo;
        if (hi > max) max = hi;
      }
    }
    return { min, max };
  }

  // バー数がペア数を上回る場合（短い音源を広い幅で表示）: 中点の線形補間
  const tc = Math.max(0, Math.min(pairCount - 1, (t0 + t1) / 2));
  const lo = Math.floor(tc);
  const hi = Math.min(lo + 1, pairCount - 1);
  const frac = tc - lo;
  let min = 0;
  let max = 0;
  for (const data of channels) {
    const minV = data[lo * 2] * (1 - frac) + data[hi * 2] * frac;
    const maxV = data[lo * 2 + 1] * (1 - frac) + data[hi * 2 + 1] * frac;
    if (minV < min) min = minV;
    if (maxV > max) max = maxV;
  }
  return { min, max };
}

/**
 * min/max ペアのピークをラウンド角バーで描画する。
 * ピーク範囲 [startPair, endPair) をレーン幅いっぱいに引き伸ばして描く。
 */
export function drawWaveformBars(
  ctx: CanvasRenderingContext2D,
  options: DrawWaveformBarsOptions,
): void {
  const {
    channels,
    x,
    y,
    width,
    height,
    barWidth = WAVEFORM_BAR_DEFAULTS.barWidth,
    barGap = WAVEFORM_BAR_DEFAULTS.barGap,
    barRadius = WAVEFORM_BAR_DEFAULTS.barRadius,
    color,
    amplitudeScale = 1,
    barPhase = 0,
  } = options;

  if (channels.length === 0 || width <= 0 || height <= 0) return;
  const pairCount = Math.floor(channels[0].length / 2);
  if (pairCount <= 0) return;

  const startPair = options.startPair ?? 0;
  const endPair = options.endPair ?? pairCount;
  const pairSpan = endPair - startPair;
  if (pairSpan <= 0) return;

  const pitch = barWidth + barGap;
  const centerY = y + height / 2;
  const amp = Math.max(1, height / 2 - 1);

  const supportsRoundRect = typeof ctx.roundRect === 'function';

  ctx.fillStyle = color;
  ctx.beginPath();

  // barPhase 分だけ格子を前倒しして、スクロールしてもバー位置が流れないようにする
  for (let bx = barPhase; bx < width; bx += pitch) {
    const t0 = startPair + ((bx - barPhase) / width) * pairSpan;
    const t1 = startPair + ((bx - barPhase + pitch) / width) * pairSpan;
    const sample = samplePairRange(channels, pairCount, t0, t1);
    if (!sample) continue;

    const minV = Math.max(-1, sample.min * amplitudeScale);
    const maxV = Math.min(1, sample.max * amplitudeScale);

    let y0 = centerY - maxV * amp;
    let barH = (maxV - minV) * amp;
    if (barH < 2) {
      y0 = centerY - 1;
      barH = 2;
    }

    const drawX = x + bx;
    const clippedW = Math.min(barWidth, width - bx);
    if (clippedW <= 0) continue;

    if (supportsRoundRect) {
      const r = Math.max(0, Math.min(barRadius, clippedW / 2, barH / 2));
      ctx.roundRect(drawX, y0, clippedW, barH, r);
    } else {
      ctx.rect(drawX, y0, clippedW, barH);
    }
  }

  ctx.fill();
}

export interface DrawWaveformBarsWindowOptions {
  /** 各チャンネルの min/max 交互配列。複数チャンネルは外包絡にマージして 1 レーンに描く */
  channels: number[][];
  /** ピーク密度（min/max ペア数 / 秒） */
  pairsPerSecond: number;
  /**
   * バー格子の原点となる Canvas x 座標（画面外可）。
   * この点が `sourceStartSeconds` に対応し、バーは原点に整列する。
   * スクロールしてもバーが流れず、クリップ移動時は波形がクリップに追従する。
   */
  originX: number;
  /** originX 時点のピーク読み出し秒（クリップの peaksOffset 等） */
  sourceStartSeconds: number;
  /** 1 秒あたりのピクセル数（ズーム倍率） */
  pixelsPerSecond: number;
  /** 描画範囲の左端（Canvas ローカル座標） */
  clipLeft: number;
  /** 描画範囲の右端（Canvas ローカル座標） */
  clipRight: number;
  /** レーンの上端（CSS px） */
  y: number;
  /** レーン高さ（CSS px） */
  height: number;
  barWidth?: number;
  barGap?: number;
  barRadius?: number;
  color: string;
  /** 振幅の表示スケール（クリップゲイン可視化等。既定 1） */
  amplitudeScale?: number;
}

/**
 * スクロール窓向けのバー描画（タイムライン用）。
 *
 * ビューポート幅の Canvas に、時間軸座標（originX + 秒 × pixelsPerSecond）で
 * バーを描く。drawWaveformBars と同じバー形状で、プレビューと見た目が揃う。
 */
export function drawWaveformBarsWindow(
  ctx: CanvasRenderingContext2D,
  options: DrawWaveformBarsWindowOptions,
): void {
  const {
    channels,
    pairsPerSecond,
    originX,
    sourceStartSeconds,
    pixelsPerSecond,
    clipLeft,
    clipRight,
    y,
    height,
    barWidth = WAVEFORM_BAR_DEFAULTS.barWidth,
    barGap = WAVEFORM_BAR_DEFAULTS.barGap,
    barRadius = WAVEFORM_BAR_DEFAULTS.barRadius,
    color,
    amplitudeScale = 1,
  } = options;

  if (
    channels.length === 0 ||
    clipRight <= clipLeft ||
    height <= 0 ||
    pixelsPerSecond <= 0 ||
    pairsPerSecond <= 0
  ) {
    return;
  }
  const pairCount = Math.floor(channels[0].length / 2);
  if (pairCount <= 0) return;

  const pitch = barWidth + barGap;
  const centerY = y + height / 2;
  const amp = Math.max(1, height / 2 - 1);
  const supportsRoundRect = typeof ctx.roundRect === 'function';

  ctx.fillStyle = color;
  ctx.beginPath();

  // 可視範囲の最初のバー index（原点より前のバーは存在しない）
  let k = Math.max(0, Math.floor((clipLeft - originX) / pitch));
  for (; ; k++) {
    const bx = originX + k * pitch;
    if (bx >= clipRight) break;

    const t0 = sourceStartSeconds + (k * pitch) / pixelsPerSecond;
    const t1 = sourceStartSeconds + ((k + 1) * pitch) / pixelsPerSecond;
    const sample = samplePairRange(
      channels,
      pairCount,
      t0 * pairsPerSecond,
      t1 * pairsPerSecond,
    );
    if (!sample) continue;

    const minV = Math.max(-1, sample.min * amplitudeScale);
    const maxV = Math.min(1, sample.max * amplitudeScale);

    let y0 = centerY - maxV * amp;
    let barH = (maxV - minV) * amp;
    if (barH < 2) {
      y0 = centerY - 1;
      barH = 2;
    }

    const clippedW = Math.min(barWidth, clipRight - bx);
    if (clippedW <= 0) continue;

    if (supportsRoundRect) {
      const r = Math.max(0, Math.min(barRadius, clippedW / 2, barH / 2));
      ctx.roundRect(bx, y0, clippedW, barH, r);
    } else {
      ctx.rect(bx, y0, clippedW, barH);
    }
  }

  ctx.fill();
}

/** peaks 未取得時のプレースホルダーバー（フラットな低振幅）を描画する */
export function drawPlaceholderBars(
  ctx: CanvasRenderingContext2D,
  options: Omit<DrawWaveformBarsOptions, 'channels' | 'startPair' | 'endPair'>,
): void {
  const { x, y, width, height, color } = options;
  const barWidth = options.barWidth ?? WAVEFORM_BAR_DEFAULTS.barWidth;
  const barGap = options.barGap ?? WAVEFORM_BAR_DEFAULTS.barGap;
  const barRadius = options.barRadius ?? WAVEFORM_BAR_DEFAULTS.barRadius;

  if (width <= 0 || height <= 0) return;

  const pitch = barWidth + barGap;
  const centerY = y + height / 2;
  const barH = Math.max(2, Math.round(height * 0.16));
  const y0 = centerY - barH / 2;
  const supportsRoundRect = typeof ctx.roundRect === 'function';

  ctx.save();
  ctx.globalAlpha = 0.45;
  ctx.fillStyle = color;
  ctx.beginPath();
  for (let bx = 0; bx < width; bx += pitch) {
    const clippedW = Math.min(barWidth, width - bx);
    if (clippedW <= 0) continue;
    if (supportsRoundRect) {
      const r = Math.max(0, Math.min(barRadius, clippedW / 2, barH / 2));
      ctx.roundRect(x + bx, y0, clippedW, barH, r);
    } else {
      ctx.rect(x + bx, y0, clippedW, barH);
    }
  }
  ctx.fill();
  ctx.restore();
}

/** Canvas を DPR 対応でセットアップして 2D コンテキストを返す（クリア済み） */
export function setupWaveformCanvas(
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
): CanvasRenderingContext2D | null {
  const dpr = window.devicePixelRatio || 1;
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  canvas.width = Math.round(w * dpr);
  canvas.height = Math.round(h * dpr);
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);
  return ctx;
}
